// src/firebase/creditPackages.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  runTransaction,
  Timestamp,
  QueryDocumentSnapshot,
  DocumentData,
} from "firebase/firestore";
import { db } from "./config";
import { withCache, invalidateCachePrefix } from "../utils/cache";

const CACHE_KEY     = "creditPackages:all";
const CACHE_TTL     = 5 * 60 * 1000; // 5 minutes — admin list
const TTL_BY_COACH  = 2 * 60 * 1000; // 2 minutes — per-coach reads

// Coarse but safe: any package write invalidates the entire namespace
// (admin list, per-coach lists, per-coach active lookup).
function invalidateCreditPackagesCache(): void {
  invalidateCachePrefix("creditPackages:");
}

// ---------------------------------------------------------------------------
// Interface
// ---------------------------------------------------------------------------

export interface CreditPackage {
  id?: string;
  coachId: string;
  coachEmail: string;
  coachDisplayName: string;
  /** "any" means the credits apply to any open competition */
  competitionId: string;
  /** "Any Open Competition" when competitionId === "any" */
  competitionName: string;
  creditsTotal: number;
  creditsUsed: number;
  amountPaid: number;
  paymentMethod: "bank_transfer";
  bankReference: string;
  createdAt: Timestamp;
  createdBy: string;
  notes: string;
  status: "active" | "exhausted" | "cancelled";
}

// ---------------------------------------------------------------------------
// WRITE
// ---------------------------------------------------------------------------

/**
 * Admin creates a new credit package for a coach.
 * Returns the new document ID.
 */
export const createCreditPackage = async (
  data: Omit<CreditPackage, "id" | "createdAt" | "creditsUsed">
): Promise<string> => {
  const docRef = await addDoc(collection(db, "creditPackages"), {
    ...data,
    creditsUsed: 0,
    createdAt: Timestamp.now(),
  });
  invalidateCreditPackagesCache();
  return docRef.id;
};

export const updateCreditPackage = async (
  packageId: string,
  updates: Partial<
    Pick<CreditPackage, "creditsTotal" | "notes" | "bankReference" | "status" | "amountPaid">
  >
): Promise<void> => {
  const ref = doc(db, "creditPackages", packageId);
  await updateDoc(ref, { ...updates });
  invalidateCreditPackagesCache();
};

export const cancelCreditPackage = async (packageId: string): Promise<void> => {
  const ref = doc(db, "creditPackages", packageId);
  await updateDoc(ref, { status: "cancelled" });
  invalidateCreditPackagesCache();
};

/**
 * Safely decrements credits in a Firestore transaction.
 * Throws if the package doesn't exist or has insufficient credits.
 * NOTE: For atomic registration + credit deduction, use createPrepaidRegistrationBatch()
 * which handles both in a single transaction. This standalone version is for admin corrections.
 */
export const consumeCredits = async (
  packageId: string,
  count: number
): Promise<void> => {
  const ref = doc(db, "creditPackages", packageId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error("Credit package not found");

    const data = snap.data() as CreditPackage;
    const remaining = data.creditsTotal - data.creditsUsed;
    if (remaining < count) {
      throw new Error(
        `Not enough credits. Available: ${remaining}, requested: ${count}`
      );
    }

    const newUsed = data.creditsUsed + count;
    const newStatus: CreditPackage["status"] =
      newUsed >= data.creditsTotal ? "exhausted" : data.status;
    tx.update(ref, { creditsUsed: newUsed, status: newStatus });
  });
  // Credits consumed — package status may have changed, invalidate cache
  invalidateCreditPackagesCache();
};

// ---------------------------------------------------------------------------
// READ
// ---------------------------------------------------------------------------

/** All packages for a given coach (coach dashboard + history). */
export const getCreditPackagesByCoach = async (
  coachId: string
): Promise<CreditPackage[]> =>
  withCache(`creditPackages:coach:${coachId}`, TTL_BY_COACH, async () => {
    const q = query(
      collection(db, "creditPackages"),
      where("coachId", "==", coachId),
      orderBy("createdAt", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as CreditPackage));
  });

/**
 * Returns the first active credit package that applies to the given
 * coach + competition (either an exact match or "any" wildcard).
 * Returns null if no applicable active package exists.
 *
 * Cached per coach (the candidate set is small — usually 0 or 1 active
 * packages), and the .find() narrows down per competition.
 */
export const getActiveCreditPackageForCoach = async (
  coachId: string,
  competitionId: string
): Promise<CreditPackage | null> => {
  const activeList = await withCache(
    `creditPackages:coach:${coachId}:active`,
    TTL_BY_COACH,
    async () => {
      const q = query(
        collection(db, "creditPackages"),
        where("coachId", "==", coachId),
        where("status", "==", "active")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(
        (d) => ({ id: d.id, ...d.data() } as CreditPackage)
      );
    }
  );

  const found = activeList.find(
    (p) =>
      (p.competitionId === "any" || p.competitionId === competitionId) &&
      p.creditsUsed < p.creditsTotal
  );
  return found ?? null;
};

/** Admin: all packages across all coaches. Capped at 200 newest so this
 * cannot become a full-collection scan as the dataset grows. */
export const getAllCreditPackages = async (): Promise<CreditPackage[]> => {
  return withCache(CACHE_KEY, CACHE_TTL, async () => {
    const q = query(
      collection(db, "creditPackages"),
      orderBy("createdAt", "desc"),
      limit(200)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as CreditPackage));
  });
};

// ---------------------------------------------------------------------------
// READ — server-side paginated (Phase 17+ scalability)
// ---------------------------------------------------------------------------

export interface CreditPackagePage {
  rows: CreditPackage[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

export type CreditPackageStatus = "active" | "exhausted" | "cancelled";

/**
 * Server-side paginated fetcher for the admin Manage Credit Packages page.
 * Pushes the status filter into Firestore so the on-the-wire payload only
 * contains rows matching the selected status. Pass the previous page's
 * `cursor` as `startAfterDoc` to extend the list.
 *
 * `pageSize` doubles as the user-visible page-size selector value
 * (10 / 25 / 50). Cache is keyed per (status, pageSize, cursorId) so
 * repeated paging within the TTL is free.
 */
export const getCreditPackagesPage = async (
  opts: {
    pageSize?: number;
    status?: CreditPackageStatus | "all";
    startAfterDoc?: QueryDocumentSnapshot<DocumentData> | null;
  } = {}
): Promise<CreditPackagePage> => {
  const pageSize = opts.pageSize ?? 50;
  const status   = opts.status ?? "all";
  const cursor   = opts.startAfterDoc ?? null;

  const constraints: any[] = [];
  if (status !== "all") constraints.push(where("status", "==", status));
  constraints.push(orderBy("createdAt", "desc"));
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(pageSize));

  // First page (no cursor) is cached so quick page-loads & filter toggles
  // don't re-hit Firestore. Subsequent pages skip the cache because cursors
  // aren't equality-comparable.
  //
  // NOTE on persistence: the cached value contains the cursor (a Firestore
  // QueryDocumentSnapshot) which is NOT serialisable. We strip it before
  // returning to the in-memory store... actually, simpler — we just keep
  // these first-page caches in memory only. Persisting the snapshot doesn't
  // help and would break "Load more".
  if (!cursor) {
    return withCache(
      `creditPackages:page:${status}:${pageSize}`,
      TTL_BY_COACH,
      async () => {
        const snap = await getDocs(query(collection(db, "creditPackages"), ...constraints));
        const docs = snap.docs;
        return {
          rows: docs.map((d) => ({ id: d.id, ...d.data() } as CreditPackage)),
          cursor: docs.length > 0 ? docs[docs.length - 1] : null,
          hasMore: docs.length === pageSize,
        };
      }
    );
  }

  const snap = await getDocs(query(collection(db, "creditPackages"), ...constraints));
  const docs = snap.docs;
  return {
    rows: docs.map((d) => ({ id: d.id, ...d.data() } as CreditPackage)),
    cursor: docs.length > 0 ? docs[docs.length - 1] : null,
    hasMore: docs.length === pageSize,
  };
};